import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  signal,
  viewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { IPropertyData } from '@lassi/webapp-shared';
import { MultiLotSearchControlComponent } from './multi-lot-search-control.component';
import { ParcelDisplayComponent } from './parcel-display.component';

@Component({
  selector: 'lib-property-info-parcel-tab',
  imports: [
    MatDividerModule,
    ParcelDisplayComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    FormsModule,
    MultiLotSearchControlComponent,
  ],
  template: `
    <div class="parcel-container">
      @if (parcels().length > 1) {
        <lib-multi-lot-search-control
          [(searchTerm)]="searchTerm"
          [searchableItems]="parcelSpis()"
          (indexChange)="onMatchedIndexChange($event)"
        />
      }

      <div class="parcels-list">
        @for (parcel of parcels(); let i = $index; track i) {
          <lib-parcel-display
            [parcel]="parcel"
            [prefix]="parcels().length > 1 ? 'Parcel ' + (i + 1) + ' - ' : ''"
            [searchTerm]="searchTerm()"
          />

          <mat-divider class="parcel-divider" />
        }
      </div>
    </div>
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;
    @use 'breakpoints' as *;

    .parcel-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }

    .search-container {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 1rem;
      background-color: #{mat.get-theme-color(theme.$light-theme, surface)};
      border-bottom: 1px solid #{mat.get-theme-color(
          theme.$light-theme,
          surface-variant
        )};

      @include handset {
        padding-bottom: 0.5rem;
      }

      .search-field {
        flex: 1;
        margin-bottom: -1.25em;
      }

      .match-controls {
        display: flex;
        align-items: center;
        gap: 4px;

        .match-count {
          white-space: nowrap;
          padding: 0 8px;
        }
      }
    }

    .parcels-list {
      flex: 1;
      overflow-y: auto;
      padding: 16px;

      .parcel-divider {
        margin: 16px 0;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyInfoParcelTabComponent {
  parcels = input.required<IPropertyData['parcels']>();

  readonly searchTerm = signal('');
  readonly parcelElements = viewChildren(ParcelDisplayComponent, {
    read: ElementRef,
  });

  parcelSpis = computed(() => {
    const parcels = this.parcels() ?? [];
    return parcels.map((parcel) => parcel.spi ?? '');
  });

  onMatchedIndexChange(parcelIndex: number): void {
    const elements = this.parcelElements();
    if (elements[parcelIndex]) {
      elements[parcelIndex].nativeElement.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
      });
    }
  }
}
